import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from '@plone/volto/helpers';
import { getApiPath, getAuthHeaders, fetchApi, isTerminologyRestricted } from '../api';
import { useRole } from '../hooks/useRole';
import { TIER1, TIER2 } from '../constants/languageTiers';
import './LandingPage.css';

/* ── Tool cards with role visibility ────────────────────────────── */

const pages = [
  {
    title: 'Review Translations',
    description: 'Review and attest translations for your assigned languages. Vote, edit, comment — every review is a formal attestation.',
    icon: 'pencil',
    to: '/polyglot/review',
    section: 'contribute',
    roles: ['contributor', 'admin'],
  },
  {
    title: 'Contributor Dashboard',
    description: 'Your personal dashboard — assigned languages, coverage across all platforms, correction stats.',
    icon: 'user',
    to: '/polyglot/dashboard',
    section: 'contribute',
    roles: ['contributor', 'admin'],
  },
  {
    title: 'Onboarding Training',
    description: 'Three short modules to understand the system, then your Official Polyglot Translator certificate.',
    icon: 'graduation-cap',
    to: '/polyglot/training',
    section: 'contribute',
    roles: ['contributor', 'admin'],
  },
  {
    title: 'Your Certificate',
    description: 'View and download your Official Polyglot Translator certificate after completing training.',
    icon: 'award',
    to: '/polyglot/certificate',
    section: 'contribute',
    roles: ['contributor', 'admin'],
  },
  {
    title: 'Coverage Dashboard',
    description: 'Heat map of translation coverage across all languages and platforms. See gaps at a glance.',
    icon: 'grid',
    to: '/polyglot/coverage',
    section: 'tools',
    roles: ['user', 'contributor', 'admin'],
  },
  {
    title: 'Translation Browser',
    description: 'Search and filter translations by platform, language, and text. Paginated with missing/translated filters.',
    icon: 'search',
    to: '/polyglot/browse',
    section: 'tools',
    roles: ['user', 'contributor', 'admin'],
  },
  {
    title: 'Glossary Search',
    description: 'Search across IATE (EU terminology, 193K entries) and Microsoft Terminology (2.5M entries).',
    icon: 'book',
    to: '/polyglot/glossary',
    section: 'tools',
    terminologyRestricted: true,
    roles: ['admin'],
  },
  {
    title: 'Conventions',
    description: 'Browse language conventions for 70 languages. Register, script, grammar patterns, plural rules.',
    icon: 'settings',
    to: '/polyglot/conventions',
    section: 'tools',
    roles: ['user', 'contributor', 'admin'],
  },
  {
    title: 'QC Findings Review',
    description: 'Work through AI-detected quality issues one at a time. Fix, dismiss, or reclassify findings.',
    icon: 'shield',
    to: '/polyglot/qc/review',
    section: 'qc',
    roles: ['admin'],
  },
  {
    title: 'QC Dashboard',
    description: 'QC findings overview: status by platform, risk heatmap, disposition breakdown, audit trail.',
    icon: 'chart',
    to: '/polyglot/qc',
    section: 'qc',
    roles: ['admin'],
  },
  {
    title: 'QC Findings Browser',
    description: 'Browse, filter, and paginate all AI-detected findings. Bulk triage by platform, language, and type.',
    icon: 'list',
    to: '/polyglot/qc/findings',
    section: 'qc',
    roles: ['admin'],
  },
  {
    title: 'Upstream Issues',
    description: 'Track issues reported upstream to Plone — .pot corrections, community patches, style guide proposals.',
    icon: 'arrow-up',
    to: '/polyglot/qc/upstream',
    section: 'qc',
    roles: ['admin'],
  },
  {
    title: 'Contributor Management',
    description: 'Manage contributors, review applications, send invitations, assign languages.',
    icon: 'users',
    to: '/polyglot/contributors',
    section: 'admin',
    roles: ['admin'],
  },
  {
    title: 'Feature Backlog',
    description: 'Track feature requests, wishes, and planned work. Prioritise, assign DRs, mark complete.',
    icon: 'lightbulb',
    to: '/polyglot/features',
    section: 'admin',
    roles: ['admin'],
  },
  {
    title: 'Apply to Contribute',
    description: 'Native speaker or experienced translator? Help improve translations for 71 languages worldwide.',
    icon: 'hand',
    to: '/polyglot/apply',
    section: 'getinvolved',
    roles: [],
  },
  {
    title: 'Community Corrections',
    description: 'Public comparison of English corrections and community vs AI translations across all languages.',
    icon: 'globe',
    to: '/community/corrections',
    section: 'getinvolved',
    roles: [],
  },
  {
    title: 'Documents',
    description: 'Architecture, workflows, regulatory compliance, linguistic strategies. Full-text searchable.',
    icon: 'file',
    to: '/docs',
    section: 'platform',
    roles: ['user', 'contributor', 'admin'],
  },
  {
    title: 'Document Hub',
    description: 'Overview of all documentation — by category, recent changes, and document statistics.',
    icon: 'folder',
    to: '/documents',
    section: 'platform',
    roles: ['user', 'contributor', 'admin'],
  },
  {
    title: 'Document Timeline',
    description: 'Chronological view of all document changes — grouped by month, filterable by type.',
    icon: 'clock',
    to: '/docs/timeline',
    section: 'platform',
    roles: ['user', 'contributor', 'admin'],
  },
  {
    title: 'Platform Changelog',
    description: 'What changed and when — the full change-controlled history, linked to its governing documents.',
    icon: 'clock',
    to: '/polyglot/changelog',
    section: 'platform',
    roles: ['user', 'contributor', 'admin'],
  },
  {
    title: 'Document Divergence',
    description: 'Documents where the database version differs from the git source — needs reconciliation.',
    icon: 'alert',
    to: '/docs/divergence',
    section: 'platform',
    roles: ['admin'],
  },
  {
    title: 'Policies',
    description: 'Platform policies: privacy, cookies, contributor agreement, GDPR compliance.',
    icon: 'policy',
    to: '/policies',
    section: 'platform',
    roles: ['user', 'contributor', 'admin'],
  },
  {
    title: 'API Reference',
    description: '74 endpoints across 11 modules — interactive reference with permissions and descriptions.',
    icon: 'code',
    to: '/api-docs',
    section: 'platform',
    roles: ['contributor', 'admin'],
  },
];

const SECTIONS = [
  { key: 'contribute', title: 'Your Translation Tools', color: 'var(--accent-teal, #00b5ad)' },
  { key: 'tools', title: 'Translation Tools', color: 'var(--accent-blue, #2185d0)' },
  { key: 'qc', title: 'Quality Control', color: 'var(--accent-red, #db2828)' },
  { key: 'admin', title: 'Administration', color: 'var(--accent-pink, #e03997)' },
  { key: 'getinvolved', title: 'Get Involved', color: 'var(--accent-teal, #00b5ad)' },
  { key: 'platform', title: 'Platform', color: 'var(--accent-purple, #6435c9)' },
];

/* ── Public hero ────────────────────────────────────────────────── */

const PublicHero = () => (
  <div className="landing-hero landing-hero--public">
    <h1>Polyglot Monster Machine</h1>
    <p className="landing-hero__subtitle">
      Open source translation management for the Plone ecosystem
    </p>

    <div className="landing-stats">
      <div className="landing-stat">
        <span className="landing-stat__number">71</span>
        <span className="landing-stat__label">Languages</span>
      </div>
      <div className="landing-stat">
        <span className="landing-stat__number">330K+</span>
        <span className="landing-stat__label">Translations</span>
      </div>
      <div className="landing-stat">
        <span className="landing-stat__number">5</span>
        <span className="landing-stat__label">Platforms</span>
      </div>
      <div className="landing-stat">
        <span className="landing-stat__number">516</span>
        <span className="landing-stat__label">English Corrections</span>
      </div>
    </div>

    <div className="landing-pitch">
      <div className="landing-pitch__item">
        <strong>Community-reviewed</strong> — translations are reviewed and attested by qualified contributors who put their name on the work
      </div>
      <div className="landing-pitch__item">
        <strong>ISO standards</strong> — risk-assessed (ISO 14971), terminology-managed (ISO 704), quality-controlled (ISO 17100)
      </div>
      <div className="landing-pitch__item">
        <strong>Public domain</strong> — all translations are CC0. Use them freely, no strings attached
      </div>
    </div>

    <div className="landing-actions">
      <Link to="/polyglot/apply" className="landing-btn landing-btn--primary">
        Apply to Contribute
      </Link>
      <Link to="/login?return_url=/polyglot" className="landing-btn landing-btn--secondary">
        Sign In
      </Link>
    </div>
  </div>
);

/* ── User hero (signed in, not a contributor) ───────────────────── */

const UserHero = () => (
  <div className="landing-hero">
    <h1>Polyglot Monster Machine</h1>
    <p className="landing-hero__subtitle">
      Translation management for 71 languages across 5 platforms
    </p>

    <div className="landing-cta-card">
      <h3>Become a Contributor</h3>
      <p>
        Native speaker or experienced translator? Contributors review translations,
        vote on quality, suggest corrections, and leave comments. Every review is a
        formal attestation — your name on professional work.
      </p>
      <Link to="/polyglot/apply" className="landing-btn landing-btn--primary">
        Apply to Contribute
      </Link>
    </div>
  </div>
);

/* ── Contributor hero ───────────────────────────────────────────── */

const ContributorHero = ({ profile }) => {
  const firstName = profile?.fullname?.split(/\s+/)[0] || 'Contributor';
  const langCount = profile?.languages?.length || 0;
  const corrections = profile?.total_contributions || 0;
  const daysSince = profile?.member_since
    ? Math.floor((Date.now() - new Date(profile.member_since).getTime()) / 86400000)
    : 0;

  return (
    <div className="landing-hero">
      <h1>Welcome back, {firstName}</h1>
      <div className="landing-stats">
        <div className="landing-stat">
          <span className="landing-stat__number">{corrections}</span>
          <span className="landing-stat__label">Corrections</span>
        </div>
        <div className="landing-stat">
          <span className="landing-stat__number">{langCount}</span>
          <span className="landing-stat__label">Languages</span>
        </div>
        <div className="landing-stat">
          <span className="landing-stat__number">{daysSince}</span>
          <span className="landing-stat__label">{daysSince === 1 ? 'Day' : 'Days'}</span>
        </div>
      </div>
    </div>
  );
};

/* ── Contributor language pills ─────────────────────────────────── */

const ContributorLanguages = ({ profile }) => {
  if (!profile?.languages?.length) return null;

  return (
    <section className="landing-section">
      <h2>Your Languages</h2>
      <p className="landing-section__subtitle">Start reviewing translations for your assigned languages</p>
      <div className="landing-pills">
        {profile.languages.map((l) => {
          const avgCoverage = l.platforms?.length
            ? Math.round(l.platforms.reduce((sum, p) => sum + (p.percentage || 0), 0) / l.platforms.length)
            : 0;
          const coverageClass = avgCoverage >= 99 ? 'pill--green' : avgCoverage >= 90 ? 'pill--olive' : 'pill--blue';
          return (
            <Link key={l.code} to={`/polyglot/review?language=${l.code}`} className={`landing-pill ${coverageClass}`}>
              <span className="landing-pill__code">{l.code}</span>
              <span className="landing-pill__name">{l.name}</span>
              <span className="landing-pill__detail">{avgCoverage}%</span>
            </Link>
          );
        })}
      </div>
    </section>
  );
};

/* ── QC language pills (admin) ──────────────────────────────────── */

const QCLanguages = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchApi(`${getApiPath()}/@polyglot/qc-review/languages`)
      .then((json) => { setData(json); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  if (loading) return <section className="landing-section"><p className="landing-loading">Loading QC languages...</p></section>;
  if (!data?.languages?.length) return null;

  // Group by the frontend's classification (constants/languageTiers), NOT
  // the API's `tier` field — the deployed backend can lag it (zh-CN mislabel,
  // BUG report 2026-07-04; backend fix rides the Phase-3 brain update).
  const tier1 = data.languages.filter((l) => TIER1.includes(l.language));
  const tier2 = data.languages.filter((l) => TIER2.includes(l.language));

  const renderPills = (langs) =>
    langs.map((l) => {
      const cls = l.total_findings > 100 ? 'pill--orange' : l.total_findings > 30 ? 'pill--yellow' : '';
      return (
        <Link key={l.language} to={`/polyglot/qc/review/work?language=${l.language}`} className={`landing-pill ${cls}`}>
          <span className="landing-pill__code">{l.language}</span>
          <span className="landing-pill__name">{l.language_name_english}</span>
          <span className="landing-pill__detail">{l.total_findings}</span>
        </Link>
      );
    });

  return (
    <section className="landing-section">
      <h2>QC Findings by Language</h2>
      <p className="landing-section__subtitle">Pick a language to triage findings one at a time</p>
      {tier1.length > 0 && (
        <div className="landing-pill-group">
          <div className="landing-pill-group__label">EU Official</div>
          <div className="landing-pills">{renderPills(tier1)}</div>
        </div>
      )}
      {tier2.length > 0 && (
        <div className="landing-pill-group">
          <div className="landing-pill-group__label">Partner</div>
          <div className="landing-pills">{renderPills(tier2)}</div>
        </div>
      )}
    </section>
  );
};

/* ── All-languages navigator ────────────────────────────────────── */

const AllLanguages = () => {
  const [languages, setLanguages] = useState(null);
  const [loading, setLoading] = useState(true);
  const TIER_SET = new Set([...TIER1, ...TIER2]);

  useEffect(() => {
    fetchApi(`${getApiPath()}/@polyglot/conventions`)
      .then((json) => { setLanguages(json.items || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  if (loading) return <section className="landing-section"><p className="landing-loading">Loading languages...</p></section>;
  if (!languages?.length) return null;

  const tier1 = languages.filter((l) => TIER1.includes(l.language_code));
  const tier2 = languages.filter((l) => TIER2.includes(l.language_code));
  const other = languages.filter((l) => !TIER_SET.has(l.language_code));

  const renderGroup = (langs) => (
    <div className="landing-pills">
      {langs.map((l) => (
        <Link key={l.language_code} to={`/polyglot/languages/${l.language_code}`} className="landing-pill">
          <span className="landing-pill__code">{l.language_code}</span>
          <span className="landing-pill__name">{l.language_name_english}</span>
        </Link>
      ))}
    </div>
  );

  return (
    <section className="landing-section">
      <h2>All Languages</h2>
      <p className="landing-section__subtitle">Browse coverage, conventions, risk, and verification per language</p>
      <div className="landing-pill-group">
        <div className="landing-pill-group__label">EU Official</div>
        {renderGroup(tier1)}
      </div>
      <div className="landing-pill-group">
        <div className="landing-pill-group__label">Partner</div>
        {renderGroup(tier2)}
      </div>
      {other.length > 0 && (
        <div className="landing-pill-group">
          <div className="landing-pill-group__label">Community ({other.length})</div>
          {renderGroup(other)}
        </div>
      )}
    </section>
  );
};

/* ── Download section ───────────────────────────────────────────── */

const Downloads = () => (
  <section className="landing-section">
    <h2>Download Locale Packages</h2>
    <p className="landing-section__subtitle">Full .po file set for all languages — one zip per platform</p>
    <div className="landing-downloads">
      {['nick', 'volto', 'volto_slate', 'volto_vlt', 'plone'].map((p) => (
        <a
          key={p}
          href={`${getApiPath()}/@polyglot/export/po?platform=${p}&language=all`}
          className="landing-btn landing-btn--download"
          onClick={(e) => {
            e.preventDefault();
            fetch(`${getApiPath()}/@polyglot/export/po?platform=${p}&language=all`, { headers: getAuthHeaders() })
              .then((res) => { if (!res.ok) throw new Error(`${res.status}`); return res.blob(); })
              .then((blob) => {
                const a = document.createElement('a');
                a.href = URL.createObjectURL(blob);
                a.download = `${p}-locales.zip`;
                a.click();
                URL.revokeObjectURL(a.href);
              })
              .catch((err) => alert(`Export failed: ${err.message}`));
          }}
        >
          {p}
        </a>
      ))}
    </div>
    <p className="landing-note">Generated from the live translation database — always reflects the latest corrections.</p>
  </section>
);

/* ── Card sections ──────────────────────────────────────────────── */

const CardSections = ({ role }) => {
  const visibleSections = SECTIONS.filter((section) => {
    const sectionPages = pages.filter(
      (p) => p.section === section.key
        && (!p.roles || p.roles.includes(role))
        && !(isTerminologyRestricted() && p.terminologyRestricted),
    );
    return sectionPages.length > 0;
  });

  return visibleSections.map((section) => {
    const sectionPages = pages.filter(
      (p) => p.section === section.key
        && (!p.roles || p.roles.includes(role))
        && !(isTerminologyRestricted() && p.terminologyRestricted),
    );
    return (
      <section key={section.key} className="landing-section">
        <h2 style={{ borderLeft: `4px solid ${section.color}`, paddingLeft: '0.5em' }}>
          {section.title}
        </h2>
        <div className="landing-cards">
          {sectionPages.map((p) => (
            <Link key={p.to} to={p.to} className="landing-card">
              <h3>{p.title}</h3>
              <p>{p.description}</p>
            </Link>
          ))}
        </div>
      </section>
    );
  });
};

/* ── Main component ─────────────────────────────────────────────── */

const LandingPage = () => {
  const { role, profile, loading } = useRole();

  if (role === 'loading') {
    return (
      <div className="landing-page">
        <p className="landing-loading">Loading...</p>
      </div>
    );
  }

  return (
    <div className="landing-page">
      <Helmet title="Polyglot Monster Machine" />

      {role === 'public' && <PublicHero />}
      {role === 'user' && <UserHero />}
      {(role === 'contributor' || role === 'admin') && <ContributorHero profile={profile} />}

      {(role === 'contributor' || role === 'admin') && profile && (
        <ContributorLanguages profile={profile} />
      )}

      {role === 'admin' && <QCLanguages />}

      <CardSections role={role} />

      {role !== 'public' && (
        <>
          <Downloads />
          <AllLanguages />
        </>
      )}

    </div>
  );
};

export default LandingPage;
